"use client";

import { VoiceModel } from "@/actions/db/get-models";
import React, { createContext, useContext } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useCurrentUser } from "@/hooks/use-current-user";

export interface IModelsContext {
  models: VoiceModel[];
  setModels: (value: VoiceModel[]) => void;
  isLoading: boolean;
}

const ModelsContext = createContext<IModelsContext | null>(null);

export function useModelsContext() {
  const modelsContext = useContext(ModelsContext);

  if (!modelsContext) {
    throw new Error("useModelsContext must be used within a ModelsProvider");
  }

  return modelsContext;
}

const ModelsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const queryClient = useQueryClient();

  const user = useCurrentUser();

  const fetchModels = async () => {
    const response = await fetch("/api/model");
    return (await response.json()) as VoiceModel[];
  };

  const { data, isLoading } = useQuery(
    ["voiceModels", user?.id],
    () => fetchModels(),
    {
      onError: (err) => {
        console.error("Error fetching models:", err);
      },
    },
  );

  const setModels = (value: VoiceModel[]) => {
    queryClient.setQueryData(["voiceModels", user?.id], value);
  };

  const modelsContextValue = {
    models: data ?? [],
    setModels: setModels,
    isLoading,
  };

  return (
    <ModelsContext.Provider value={modelsContextValue}>
      {children}
    </ModelsContext.Provider>
  );
};

export default ModelsProvider;
